/* Лента карточек 132 × 176 (BotCore.styleRail/railCard) и лист «Все …» (BotCore.gridSheet), APP-SPEC-5 §1.2:
   нажатие выбирает, удержание (или контекстное меню) — пример; в листе поиск при числе карточек больше 12,
   вкладки, ряд тем (повторное нажатие снимает тему), пустое состояние и «Готово». */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { uiFormat, uiText } from '../../../i18n';
import { artworkUrl } from '../../studio/studio-art-model';
import { StudioIcon } from '../../studio/StudioIcon';
import { StudioSheet } from '../../studio/StudioSheet';
import './rail.css';

export interface RailItem {
  id: string;
  name: string;
  sub?: string;
  image?: string | null;
  /** мозаика обложки (набор сцен, несколько картинок) */
  images?: string[];
  badge?: string;
  count?: string;
  themes?: string[];
  popularity?: number;
  isNew?: boolean;
  search?: string;
}

export function RailCard({
  item,
  on,
  locked,
  onPick,
  onPeek,
  children,
  className = '',
}: {
  item: RailItem;
  on: boolean;
  locked: boolean;
  onPick(): void;
  onPeek?(): void;
  children?: ReactNode;
  className?: string;
}) {
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const held = useRef(false);
  useEffect(() => () => clearTimeout(timer.current), []);
  const cancel = () => clearTimeout(timer.current);
  const pictures = (item.images?.length ? item.images : item.image ? [item.image] : []).map(artworkUrl).filter((url): url is string => !!url);
  return (
    <button
      type="button"
      className={`bcs-c shk-card${on ? ' on' : ''} ${className}`}
      aria-pressed={on}
      aria-label={item.count ? `${item.name} · ${item.count}` : item.name}
      disabled={locked}
      onPointerDown={() => {
        held.current = false;
        if (!onPeek) return;
        cancel();
        timer.current = setTimeout(() => {
          held.current = true;
          onPeek();
        }, 450);
      }}
      onPointerUp={cancel}
      onPointerLeave={cancel}
      onPointerCancel={cancel}
      onContextMenu={event => {
        if (!onPeek) return;
        event.preventDefault();
        onPeek();
      }}
      onClick={event => {
        if (event.detail === 0 || !held.current) onPick();
      }}
    >
      <span className={`pc${pictures.length > 1 ? ' mos' : ''}`}>
        {pictures.length ? pictures.slice(0, 4).map(url => <img key={url} src={url} alt="" draggable={false} />) : children ?? <span className="bcs-no"><StudioIcon kind="image" /></span>}
        {pictures.length > 0 && children}
        {item.badge && <span className="bcs-badge">{uiText(item.badge)}</span>}
        {item.count && <span className="ss-n">{item.count}</span>}
        <i className="ck" aria-hidden="true">
          <StudioIcon kind="check" />
        </i>
      </span>
      <b>{item.name}</b>
      {item.sub && <small>{item.sub}</small>}
    </button>
  );
}

export function RailHeader({ title, total, onAll, locked }: { title: string; total: number; onAll?(): void; locked?: boolean }) {
  return (
    <div className="glbl bcs-gl">
      {uiText(title)}
      {onAll && total > 0 && (
        <button type="button" className="lnk" disabled={locked} onClick={onAll} aria-label={uiFormat('Все: {0}', total)}>
          {uiText('Все')} <span className="mono">{total}</span>
          <StudioIcon kind="chev" />
        </button>
      )}
    </div>
  );
}

const fold = (value: string) => value.toLocaleLowerCase().replace(/ё/g, 'е');
export function GridSheet({
  title,
  items,
  isOn,
  onPick,
  onPeek,
  tabs,
  themes = [],
  locked,
  done,
  onClose,
  header,
}: {
  title: string;
  items: RailItem[];
  isOn(id: string): boolean;
  onPick(item: RailItem): void;
  onPeek?(item: RailItem): void;
  /** вкладки: id и фильтр; первая — «Все» */
  tabs: Array<{ id: string; label: string; filter(item: RailItem): boolean }>;
  themes?: string[];
  locked: boolean;
  done?: string;
  onClose(): void;
  header?: ReactNode;
}) {
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState(tabs[0]?.id ?? 'all');
  const [theme, setTheme] = useState('');
  const active = tabs.find(item => item.id === tab) ?? tabs[0];
  const shown = items.filter(
    item =>
      (!query.trim() || fold(`${item.name} ${item.sub ?? ''} ${item.search ?? ''}`).includes(fold(query.trim()))) &&
      (!active || active.filter(item)) &&
      (!theme || item.themes?.includes(theme)),
  );
  return (
    <StudioSheet
      title={title}
      onClose={onClose}
      footer={
        done ? (
          <button type="button" className="btn" onClick={onClose}>
            {done}
          </button>
        ) : undefined
      }
    >
      <div className="shk-grid-head">
        <h2>{uiText(title)}</h2>
        {items.length > 12 && (
          <label className="search">
            <StudioIcon kind="search" />
            <input type="search" value={query} placeholder={uiText('Найти')} aria-label={uiFormat('Найти: {0}', uiText(title))} onChange={event => setQuery(event.target.value)} />
          </label>
        )}
        {tabs.length > 1 && (
          <div className="seg sm" role="tablist" aria-label={uiText('Вкладки')}>
            {tabs.map(item => (
              <button key={item.id} type="button" role="tab" aria-selected={item.id === tab} className={item.id === tab ? 'on' : ''} onClick={() => setTab(item.id)}>
                {item.label}
              </button>
            ))}
          </div>
        )}
        {!!themes.length && (
          <div className="chips shk-themes" role="group" aria-label={uiText('Темы')}>
            {themes.map(value => (
              <button key={value} type="button" className={`chip${theme === value ? ' on' : ''}`} aria-pressed={theme === value} onClick={() => setTheme(current => (current === value ? '' : value))}>
                {uiText(value)}
              </button>
            ))}
          </div>
        )}
        {header}
      </div>
      <div className="pf-gr shk-grid-cards">
        {shown.map(item => (
          <RailCard key={item.id} item={item} on={isOn(item.id)} locked={locked} onPick={() => onPick(item)} onPeek={onPeek ? () => onPeek(item) : undefined} />
        ))}
      </div>
      {!shown.length && (
        <p className="shk-empty" role="status">
          <b>{uiText('Ничего не нашлось')}</b>
          <span>{uiText('Попробуйте другое слово или сбросьте тему')}</span>
        </p>
      )}
      {onPeek && <p className="shk-holdhint">{uiText('Нажмите, чтобы выбрать · Удерживайте, чтобы посмотреть примеры')}</p>}
    </StudioSheet>
  );
}
